import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
    ChevronLeft, 
    Phone, 
    MessageCircle,
    MapPin,
    Clock,
    Instagram,
    Facebook,
    Mail,
    Navigation,
    ExternalLink
} from 'lucide-react';
import { useSettings } from '../components/ClientLayout';

// ============================================================
// ALDRIN'S RESTO - CONTACT PAGE
// Design: Fond blanc, Texte vert foncé, Logo officiel
// ============================================================

// Logo officiel
const LOGO_URL = 'https://customer-assets.emergentagent.com/job_resto-stock-manager-3/artifacts/0l7b747s_9F91CBF8-DC1E-49E7-9C24-96206851AEA7.PNG';

// Couleur vert foncé du logo
const BRAND_GREEN = '#1a3d2c';

const ContactCard = ({ icon: Icon, title, value, href, external = false }) => {
    const Content = () => (
        <motion.div
            whileTap={{ scale: 0.98 }}
            className="flex items-center gap-4 p-4 sm:p-5 bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-all duration-300 group"
        >
            <div 
                className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: `${BRAND_GREEN}10` }}
            >
                <Icon className="w-6 h-6 sm:w-7 sm:h-7" style={{ color: BRAND_GREEN }} />
            </div>
            <div className="flex-1 min-w-0">
                <p className="text-xs sm:text-sm text-gray-400">{title}</p>
                <p className="text-gray-800 font-semibold text-sm sm:text-base truncate">{value}</p>
            </div>
            {href && (
                <ExternalLink className="w-5 h-5 text-gray-300 group-hover:text-gray-500 transition-colors flex-shrink-0" />
            )}
        </motion.div>
    );
    
    if (href) {
        return (
            <a href={href} target={external ? "_blank" : "_self"} rel={external ? "noopener noreferrer" : ""}>
                <Content />
            </a>
        );
    }
    return <Content />;
};

const HoursCard = ({ hours }) => (
    <div className="p-4 sm:p-5 bg-white rounded-2xl border border-gray-100 shadow-sm">
        <div className="flex items-center gap-3 mb-4">
            <div 
                className="w-10 h-10 rounded-xl flex items-center justify-center"
                style={{ backgroundColor: `${BRAND_GREEN}10` }}
            >
                <Clock className="w-5 h-5" style={{ color: BRAND_GREEN }} />
            </div>
            <h3 className="font-semibold" style={{ color: BRAND_GREEN }}>Horaires d'ouverture</h3>
        </div>
        <div className="space-y-3">
            {hours.map((item, i) => (
                <div key={i} className="flex justify-between items-center text-sm sm:text-base">
                    <span className="text-gray-500">{item.days}</span>
                    <span className="text-gray-800 font-medium">{item.time}</span>
                </div>
            ))}
        </div>
    </div>
);

const SocialLink = ({ icon: Icon, href, label }) => (
    <a href={href} target="_blank" rel="noopener noreferrer" className="flex-1">
        <motion.div
            whileTap={{ scale: 0.95 }}
            className="flex items-center justify-center gap-2 py-3.5 sm:py-4 bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-all duration-300"
        >
            <Icon className="w-5 h-5" style={{ color: BRAND_GREEN }} />
            <span className="text-gray-700 font-medium text-sm sm:text-base">{label}</span>
        </motion.div>
    </a>
);

const MapSection = ({ address, mapsUrl, plusCode }) => (
    <div className="rounded-2xl overflow-hidden border border-gray-100 shadow-sm bg-white">
        <div className="aspect-video bg-gray-100 relative">
            {/* Carte Google Maps statique avec le Plus Code */}
            <iframe
                title="Localisation Aldrin's Resto"
                src={`https://www.google.com/maps?q=9FQH%2BHH+Libreville,+Gabon&output=embed`}
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                className="absolute inset-0"
            />
            {/* Overlay pour ouvrir dans Maps */}
            <a 
                href={mapsUrl} 
                target="_blank" 
                rel="noopener noreferrer" 
                className="absolute bottom-3 right-3 flex items-center gap-2 px-4 py-2 text-white font-bold rounded-full text-sm shadow-lg hover:scale-105 transition-transform" 
                style={{ backgroundColor: BRAND_GREEN }}
            >
                <Navigation className="w-4 h-4" />
                Itinéraire
            </a>
        </div>
        <div className="p-4 bg-white">
            <div className="flex items-center gap-2 mb-1">
                <MapPin className="w-4 h-4" style={{ color: BRAND_GREEN }} />
                <span className="font-medium text-gray-800">Aldrin's Resto</span>
            </div>
            <p className="text-gray-500 text-sm">{address}</p>
            {plusCode && (
                <p className="text-xs text-gray-400 mt-1">
                    Code Plus: <span className="font-mono">{plusCode}</span>
                </p>
            )}
        </div>
    </div>
);

const ClientContactPage = () => {
    const { settings } = useSettings();
    
    // Position exacte du restaurant: Plus Code 9FQH+HH Libreville, Gabon
    const GOOGLE_MAPS_PLUS_CODE = "9FQH+HH";
    const GOOGLE_MAPS_URL = "https://maps.google.com/?q=9FQH%2BHH+Libreville,+Gabon";
    const GOOGLE_MAPS_EMBED_URL = `https://www.google.com/maps/embed/v1/place?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8&q=${encodeURIComponent("9FQH+HH Libreville, Gabon")}&zoom=17`;
    
    const info = {
        name: settings.restaurant_name || "Aldrin's Resto",
        phone: "+241 77 71 27 78",
        whatsapp: "+241 77 71 27 78",
        email: "aschrislay@gmail.com",
        address: settings.address || "Damas, Libreville - 4ème carrefour, après Prix Import d'Awendjé",
        googleMapsUrl: GOOGLE_MAPS_URL,
        plusCode: GOOGLE_MAPS_PLUS_CODE,
        hours: [
            { days: "Lundi - Samedi", time: "08h00 - 19h00" },
            { days: "Dimanche", time: "Fermé" }
        ],
        social: settings.social || {
            instagram: "https://instagram.com/aldrins_restaurant",
            facebook: "https://facebook.com/aldrins.restaurant"
        }
    };
    
    const whatsappNumber = info.whatsapp.replace(/[^0-9]/g, '');
    const whatsappLink = `https://wa.me/${whatsappNumber}?text=Bonjour, je souhaite avoir des informations sur ${info.name}`;
    
    return (
        <div className="min-h-screen bg-gray-50 pb-8">
            {/* Header */}
            <div className="sticky top-0 z-30 bg-white shadow-sm">
                {/* Logo */}
                <div className="flex justify-center py-4 border-b border-gray-100">
                    <Link to="/client">
                        <img src={LOGO_URL} alt="Aldrin's Resto" className="h-16 sm:h-20 object-contain" />
                    </Link>
                </div>
                
                <div className="px-4 py-4">
                    <div className="flex items-center gap-4">
                        <Link to="/client" className="p-2 -ml-2 rounded-full hover:bg-gray-100 transition-colors">
                            <ChevronLeft className="w-6 h-6" style={{ color: BRAND_GREEN }} />
                        </Link>
                        <div>
                            <h1 className="text-xl sm:text-2xl font-bold" style={{ color: BRAND_GREEN }}>
                                Nous contacter
                            </h1>
                            <p className="text-sm text-gray-500">Aldrin's Resto</p>
                        </div>
                    </div>
                </div>
            </div>
            
            {/* Content */}
            <div className="px-4 py-6 max-w-lg mx-auto space-y-5 sm:space-y-6">
                {/* WhatsApp */}
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-3">
                    <a href={whatsappLink} target="_blank" rel="noopener noreferrer">
                        <motion.div
                            whileTap={{ scale: 0.98 }}
                            className="flex items-center gap-4 p-4 sm:p-5 rounded-2xl transition-all duration-300"
                            style={{ backgroundColor: `${BRAND_GREEN}10`, border: `2px solid ${BRAND_GREEN}30` }}
                        >
                            <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: BRAND_GREEN }}>
                                <MessageCircle className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
                            </div>
                            <div className="flex-1">
                                <p className="text-xs sm:text-sm font-medium" style={{ color: BRAND_GREEN }}>Écrivez-nous sur</p>
                                <p className="font-bold text-lg" style={{ color: BRAND_GREEN }}>WhatsApp</p>
                            </div>
                            <ExternalLink className="w-5 h-5 flex-shrink-0" style={{ color: `${BRAND_GREEN}60` }} />
                        </motion.div>
                    </a>
                    
                    <ContactCard icon={Phone} title="Téléphone" value={info.phone} href={`tel:${info.phone}`} />
                    <ContactCard icon={Mail} title="Email" value={info.email} href={`mailto:${info.email}`} />
                </motion.div>
                
                {/* Hours */}
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
                    <HoursCard hours={info.hours} />
                </motion.div>
                
                {/* Map */}
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
                    <h3 className="font-semibold mb-3 flex items-center gap-2" style={{ color: BRAND_GREEN }}>
                        <MapPin className="w-5 h-5" />
                        Notre adresse
                    </h3>
                    <MapSection address={info.address} mapsUrl={info.googleMapsUrl} plusCode={info.plusCode} />
                </motion.div>
                
                {/* Social */}
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
                    <h3 className="font-semibold mb-3" style={{ color: BRAND_GREEN }}>Suivez-nous</h3>
                    <div className="flex gap-3">
                        <SocialLink icon={Instagram} href={info.social.instagram} label="Instagram" />
                        <SocialLink icon={Facebook} href={info.social.facebook} label="Facebook" />
                    </div>
                </motion.div>
                
                {/* CTA */}
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }} className="pt-4">
                    <Link to="/client/reservation">
                        <button className="w-full py-4 text-white font-bold rounded-xl transition-all active:scale-[0.98] shadow-lg" style={{ backgroundColor: BRAND_GREEN }}>
                            Réserver une table
                        </button>
                    </Link>
                </motion.div>
            </div>
        </div>
    );
};

export default ClientContactPage;
